/**
 * Verbal brand logo — "Cursor-L" wordmark.
 *
 * The word "verba" is followed by a blinking vertical bar cursor colored coral.
 * Together they read as "verbal" — the word is always in process, never complete.
 *
 * Two modes:
 *   "static"     — "verba" appears immediately with blinking cursor (default)
 *   "typewriter" — letters type out one by one then cursor blinks in
 *
 * Renders an inline SVG so the cursor animation is self-contained.
 * Size is controlled via the `className` prop (use Tailwind `h-*` utilities).
 *
 * SVG coordinate system:
 *   ViewBox: 0 0 116 40
 *   Font: DM Serif Display 400, 40px, tracking -0.02em
 *   "verba" measured width ≈ 104px at 40px font size
 *   Cursor rect: x=105, y=3, width=5, height=29 (cap height to baseline)
 *
 * Legibility fix (PR A): A static "l" glyph in currentColor sits beneath the
 * animated cursor rect so the full word "verbal" is always legible — even when
 * the cursor is at 0.3 opacity or the animation is disabled by prefers-reduced-motion.
 * The animated coral rect overlays the "l" to add brand personality on top.
 */

import { useId } from 'react';

interface VerbalLogoProps {
  /** Extra Tailwind classes — use h-* to control size */
  className?: string;
  /** Whether to animate the cursor blink. Defaults to true. */
  animated?: boolean;
  /** "static" = Direction B (default), "typewriter" = Direction A */
  mode?: 'static' | 'typewriter';
  /** @deprecated — control text color via className (fill uses currentColor) */
  inverted?: boolean;
}

export function VerbalLogo({
  className = 'h-6',
  animated = true,
  mode = 'static',
  inverted = false,
}: VerbalLogoProps): React.ReactElement {
  const cursorColor = 'var(--primary)';

  // Typewriter: 5 letters × 180ms each = 900ms total reveal
  // Clip rect animates from width=0 to width=106 over 900ms
  const typewriterDuration = '0.9s';
  // Unique id per instance — prevents clipPath id collision when multiple logos render simultaneously
  const uid = useId();
  const clipId = `verbal-typewriter-clip-${uid}`;

  return (
    <svg
      viewBox="0 0 116 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label="Verbal"
    >
      <defs>
        <style>{`
          @keyframes verbal-cursor-blink {
            0%, 45% { opacity: 1; }
            55%, 100% { opacity: 0.3; }
          }
          .verbal-cursor-animated {
            animation: verbal-cursor-blink 1.2s ease-in-out infinite;
          }
          @media (prefers-reduced-motion: reduce) {
            .verbal-cursor-animated {
              animation: none;
              opacity: 1;
            }
          }
          @keyframes verbal-cursor-cycle {
            0%, 100% { fill: var(--primary); }
            25%      { fill: transparent; }
            50%      { fill: var(--success); }
            75%      { fill: transparent; }
          }
          .verbal-cursor-cycle {
            animation: verbal-cursor-cycle 3s ease-in-out infinite;
          }
          @media (prefers-reduced-motion: reduce) {
            .verbal-cursor-cycle {
              animation: none;
              fill: var(--color-salmon);
            }
          }
        `}</style>

        {mode === 'typewriter' && (
          <clipPath id={clipId}>
            <rect x="0" y="0" height="40" width="0">
              <animate
                attributeName="width"
                from="0"
                to="106"
                dur={typewriterDuration}
                calcMode="discrete"
                keyTimes="0;0.2;0.4;0.6;0.8;1"
                values="0;20.8;41.6;62.4;83.2;106"
                fill="freeze"
                begin="0s"
              />
            </rect>
          </clipPath>
        )}
      </defs>

      {/* Word text — "verba", clipped for typewriter mode */}
      <text
        x="1"
        y="32"
        fontFamily="var(--font-serif-display), 'DM Serif Display', Georgia, serif"
        fontSize="40"
        fontWeight="400"
        letterSpacing="-0.025em"
        fill="currentColor"
        clipPath={mode === 'typewriter' ? `url(#${clipId})` : undefined}
      >
        verba
      </text>

      {/*
       * Static "l" glyph — legibility anchor (PR A fix).
       *
       * The animated cursor rect overlays this glyph. When the cursor blinks
       * to 0.3 opacity (or animation is off), the "l" beneath ensures the
       * full word "verbal" always reads correctly at all sizes.
       *
       * x=104 aligns with where "l" falls after "verba" at -0.025em tracking.
       * In typewriter mode the "l" is hidden until the cursor appears (opacity=0,
       * then revealed when the cursor animation begins).
       */}
      {mode === 'typewriter' ? (
        <text
          x="104"
          y="32"
          fontFamily="var(--font-serif-display), 'DM Serif Display', Georgia, serif"
          fontSize="40"
          fontWeight="400"
          letterSpacing="-0.025em"
          fill="currentColor"
          opacity="0"
        >
          l
          <animate
            attributeName="opacity"
            from="0"
            to="1"
            dur="0.001s"
            begin={typewriterDuration}
            fill="freeze"
          />
        </text>
      ) : (
        <text
          x="104"
          y="32"
          fontFamily="var(--font-serif-display), 'DM Serif Display', Georgia, serif"
          fontSize="40"
          fontWeight="400"
          letterSpacing="-0.025em"
          fill="currentColor"
        >
          l
        </text>
      )}

      {/* Animated cursor rect — overlays the static "l" above for brand personality */}
      {mode === 'typewriter' ? (
        /* In typewriter mode: cursor starts hidden, appears after text finishes */
        <rect
          x="105"
          y="3"
          width="5"
          height="29"
          fill={cursorColor}
          opacity="0"
          className={animated ? 'verbal-cursor-animated verbal-cursor-cycle' : undefined}
        >
          <animate
            attributeName="opacity"
            from="0"
            to="1"
            dur="0.001s"
            begin={typewriterDuration}
            fill="freeze"
          />
        </rect>
      ) : (
        <rect
          x="105"
          y="3"
          width="5"
          height="29"
          fill={cursorColor}
          className={animated ? 'verbal-cursor-animated verbal-cursor-cycle' : undefined}
        />
      )}
    </svg>
  );
}
